import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  LogOut, 
  BarChart3,
  FileText,
  Settings,
  ChevronRight,
  ChevronDown,
  Mail,
  Phone,
  Palette,
  Menu,
  Users,
  Euro,
  ExternalLink,
  Copy,
  Check,
  X,
  Save,
  ArrowLeft,
  GripVertical,
  Upload,
  Image,
  Code
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth helper
const getToken = () => localStorage.getItem('admin_token');
const setToken = (token) => localStorage.setItem('admin_token', token);
const removeToken = () => localStorage.removeItem('admin_token');

const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };
  
  const response = await fetch(`${API_URL}${url}`, { ...options, headers });
  
  if (response.status === 401) {
    removeToken();
    window.location.href = '/admin';
  }
  
  return response;
};

// Password Change Modal
const ChangePasswordModal = ({ isOpen, onClose, t = {} }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Default Finnish if t not provided
  const texts = {
    changePassword: t.changePassword || 'Vaihda salasana',
    currentPassword: t.currentPassword || 'Nykyinen salasana',
    newPassword: t.newPassword || 'Uusi salasana',
    confirmPassword: t.confirmPassword || 'Vahvista uusi salasana',
    cancel: t.cancel || 'Peruuta',
    changingPassword: t.changingPassword || 'Vaihdetaan...',
    passwordsDontMatch: t.passwordsDontMatch || 'Uudet salasanat eivät täsmää',
    passwordTooShort: t.passwordTooShort || 'Salasanan tulee olla vähintään 6 merkkiä',
    passwordChanged: t.passwordChanged || 'Salasana vaihdettu!',
    error: t.error || 'Virhe'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(texts.passwordsDontMatch);
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(texts.passwordTooShort);
      return;
    }
    
    setLoading(true);
    try {
      const response = await authFetch('/api/admin/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(texts.passwordChanged);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onClose();
      } else {
        toast.error(data.detail || texts.error);
      }
    } catch (err) {
      toast.error(texts.error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-stone-900">{texts.changePassword}</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              {texts.currentPassword}
            </label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              {texts.newPassword}
            </label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              {texts.confirmPassword}
            </label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {texts.cancel}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-amber-500 hover:bg-amber-600"
            >
              {loading ? texts.changingPassword : texts.changePassword}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Login Component
const LoginForm = ({ onLogin, t = {} }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const endpoint = isSetup ? '/api/admin/setup' : '/api/admin/login';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (isSetup) {
          toast.success(t.adminCreated || 'Admin-käyttäjä luotu! Kirjaudu sisään.');
          setIsSetup(false);
        } else {
          setToken(data.token);
          onLogin(data.username);
          toast.success(t.loginSuccess || 'Kirjautuminen onnistui!');
        }
      } else {
        toast.error(data.detail || t.loginError || 'Virhe kirjautumisessa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900">JuhlaLaskuri Admin</h1>
          <p className="text-stone-500 mt-2">
            {isSetup ? (t.createAdmin || 'Luo admin-käyttäjä') : (t.loginSubtitle || 'Kirjaudu hallintapaneeliin')}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.username || 'Käyttäjänimi'}</label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="border-stone-200"
              placeholder="admin"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.password || 'Salasana'}</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="border-stone-200"
              placeholder="••••••••"
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-3"
          >
            {loading ? (t.loggingIn || 'Ladataan...') : (isSetup ? (t.createUser || 'Luo käyttäjä') : (t.login || 'Kirjaudu'))}
          </Button>
        </form>
        
        <button
          onClick={() => setIsSetup(!isSetup)}
          className="w-full text-center text-sm text-stone-500 hover:text-amber-600 mt-4"
        >
          {isSetup ? (t.backToLogin || 'Takaisin kirjautumiseen') : (t.firstTime || 'Ensimmäinen kerta? Luo admin-käyttäjä')}
        </button>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ stats, companies, onSelectCompany, t = {} }) => {
  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{stats.total_companies}</p>
              <p className="text-sm text-stone-500">{t.customers || 'Asiakkaat'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{stats.active_companies}</p>
              <p className="text-sm text-stone-500">{t.activeCompanies || 'Aktiivisia'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{stats.total_quotes}</p>
              <p className="text-sm text-stone-500">{t.totalQuotes || 'Tarjouspyyntöjä'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{stats.new_quotes}</p>
              <p className="text-sm text-stone-500">{t.newQuotes || 'Uusia'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Companies List */}
      <div className="bg-white rounded-xl border border-stone-200">
        <div className="p-6 border-b border-stone-200">
          <h2 className="text-lg font-semibold text-stone-900">{t.customers || 'Asiakkaat'}</h2>
        </div>
        <div className="divide-y divide-stone-100">
          {companies.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              {t.noCompanies || 'Ei vielä asiakkaita. Lisää ensimmäinen asiakas!'}
            </div>
          ) : (
            companies.map((company) => (
              <div 
                key={company.id}
                onClick={() => onSelectCompany(company)}
                className="p-4 flex items-center justify-between hover:bg-stone-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: company.primary_color }}
                  >
                    {company.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">{company.name}</h3>
                    <p className="text-sm text-stone-500">{company.slug} • {company.menu_items?.length || 0} {t.products || 'tuotetta'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${company.active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                    {company.active ? (t.active || 'Aktiivinen') : (t.inactive || 'Ei aktiivinen')}
                  </span>
                  <ChevronRight className="w-5 h-5 text-stone-400" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Company Form Component (Basic Info)
const CompanyForm = ({ company, onSave, onCancel, t = {} }) => {
  const [formData, setFormData] = useState({
    slug: company?.slug || '',
    name: company?.name || '',
    email: company?.email || '',
    phone: company?.phone || '',
    address: company?.address || '',
    logo_url: company?.logo_url || '',
    primary_color: company?.primary_color || '#f59e0b',
    secondary_color: company?.secondary_color || '#1c1917',
    min_guests: company?.min_guests || 10,
    max_guests: company?.max_guests || 500,
    delivery_fee: company?.delivery_fee || 50,
    language: company?.language || 'fi',
    allow_custom_menu: company?.allow_custom_menu !== false,
    active: company?.active !== false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const url = company 
        ? `/api/companies/${company.id}`
        : '/api/companies';
      const method = company ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(company ? (t.customerUpdated || 'Asiakas päivitetty!') : (t.customerCreated || 'Asiakas luotu!'));
        onSave(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || t.saveError || 'Virhe tallennuksessa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <h2 className="text-lg font-semibold text-stone-900 mb-6">
        {company ? (t.editBasicInfo || 'Muokkaa perustietoja') : (t.addNewCustomer || 'Lisää uusi asiakas')}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.companyNameRequired || 'Yrityksen nimi *'}</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Ravintola Esimerkki"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.slugRequired || 'Slug (URL-tunnus) *'}</label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
              required
              disabled={!!company}
              placeholder="ravintola-esimerkki"
            />
            <p className="text-xs text-stone-500 mt-1">{t.slugHelp || 'Käytetään URL:ssa: /laskuri/'}{formData.slug || 'slug'}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.emailForQuotes || 'Sähköposti (tarjouspyyntöihin) *'}</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="tilaukset@ravintola.fi"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.phone || 'Puhelin'}</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+358 40 123 4567"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">{t.calculatorLanguage || 'Laskurin kieli'}</label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, language: 'fi' })}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                formData.language === 'fi' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-amber-300'
              }`}
            >
              <span className="text-lg">🇫🇮</span>
              <span>Suomi</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, language: 'en' })}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                formData.language === 'en' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-amber-300'
              }`}
            >
              <span className="text-lg">🇬🇧</span>
              <span>English</span>
            </button>
          </div>
          <p className="text-xs text-stone-500 mt-1">{t.calculatorLanguageHelp || 'Tämä kieli näkyy asiakkaan laskurissa'}</p>
        </div>

        <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
          <div>
            <label className="block text-sm font-medium text-stone-700">{t.allowCustomMenu || 'Salli "Rakenna oma menu"'}</label>
            <p className="text-xs text-stone-500 mt-0.5">{t.allowCustomMenuHelp || 'Jos pois päältä, asiakas voi valita vain valmiita menuita'}</p>
          </div>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, allow_custom_menu: !formData.allow_custom_menu })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.allow_custom_menu ? 'bg-amber-500' : 'bg-stone-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.allow_custom_menu ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">{t.logo || 'Logo'}</label>
          <div className="space-y-3">
            {/* Logo Preview */}
            {formData.logo_url && (
              <div className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg border">
                <img 
                  src={formData.logo_url.startsWith('/api') ? `${API_URL}${formData.logo_url}` : formData.logo_url} 
                  alt="Logo preview" 
                  className="h-12 w-auto object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <span className="text-sm text-stone-500 truncate flex-1">{formData.logo_url}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, logo_url: '' })}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Upload Button */}
            <div className="flex gap-2">
              <label className="flex-1">
                <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors">
                  <Upload className="w-5 h-5 text-stone-400" />
                  <span className="text-sm text-stone-600">{t.uploadLogo || 'Lataa logo'}</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    // Check file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error(t.fileTooLarge || 'Tiedosto on liian suuri (max 5MB)');
                      return;
                    }
                    
                    const formDataUpload = new FormData();
                    formDataUpload.append('file', file);
                    
                    try {
                      const token = getToken();
                      const response = await fetch(`${API_URL}/api/upload`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`
                        },
                        body: formDataUpload
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        setFormData({ ...formData, logo_url: data.url });
                        toast.success(t.logoUploaded || 'Logo ladattu!');
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || t.uploadFailed || 'Lataus epäonnistui');
                      }
                    } catch (err) {
                      toast.error(t.uploadFailed || 'Lataus epäonnistui');
                    }
                    
                    // Reset input
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            
            {/* Or paste URL */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-400">{t.orPasteUrl || 'tai'}</span>
              <Input
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder={t.pasteUrl || 'Liitä URL: https://example.com/logo.png'}
                className="flex-1 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.primaryColor || 'Pääväri'}</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.secondaryColor || 'Toissijainen väri'}</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.minGuests || 'Min. vierasmäärä'}</label>
            <Input
              type="number"
              value={formData.min_guests}
              onChange={(e) => setFormData({ ...formData, min_guests: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.maxGuests || 'Max. vierasmäärä'}</label>
            <Input
              type="number"
              value={formData.max_guests}
              onChange={(e) => setFormData({ ...formData, max_guests: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{t.deliveryFee || 'Toimitusmaksu (€)'}</label>
            <Input
              type="number"
              step="0.5"
              value={formData.delivery_fee}
              onChange={(e) => setFormData({ ...formData, delivery_fee: parseFloat(e.target.value) })}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="active"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="rounded"
          />
          <label htmlFor="active" className="text-sm text-stone-700">{t.isActive || 'Aktiivinen (laskuri näkyy)'}</label>
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
          >
            {loading ? (t.saving || 'Tallennetaan...') : (t.save || 'Tallenna')}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            className="rounded-xl"
          >
            {t.cancel || 'Peruuta'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Menu Editor Component
const MenuEditor = ({ company, onSave, onCancel, t = {} }) => {
  const [menuItems, setMenuItems] = useState(company?.menu_items || []);
  const [loading, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [disabledCategories, setDisabledCategories] = useState(company?.disabled_categories || []);

  const categories = [
    { id: 'alkuruoat', name: t.starters || 'Alkuruoat', subcategories: [] },
    { id: 'salaatit', name: t.salads || 'Salaatit', subcategories: [] },
    { id: 'paaruoat', name: t.mainCourses || 'Pääruoat', subcategories: [
      { id: 'possu', name: t.pork || 'Possu' },
      { id: 'harka', name: t.beef || 'Härkä' },
      { id: 'kana', name: t.chicken || 'Kana' },
      { id: 'kala', name: t.fish || 'Kala' },
      { id: 'kasvis', name: t.vegetarian || 'Kasvis' },
      { id: 'keitto', name: t.soups || 'Keitot' },
    ]},
    { id: 'lisukkeet', name: t.sides || 'Lisukkeet', subcategories: [] },
    { id: 'leivat', name: t.breads || 'Leivät', subcategories: [] },
    { id: 'jalkiruoat', name: t.desserts || 'Jälkiruoat', subcategories: [] },
    { id: 'juomat', name: t.drinks || 'Juomat', subcategories: [] },
  ];

  const getItemsByCategory = (categoryId, subcategoryId = null) => {
    return menuItems.filter(item => {
      if (subcategoryId) {
        return item.category === categoryId && item.subcategory === subcategoryId;
      }
      return item.category === categoryId && !item.subcategory;
    });
  };

  const addItem = (categoryId, subcategoryId = null) => {
    const newItem = {
      id: `new-${Date.now()}`,
      name: '',
      price: 0,
      category: categoryId,
      subcategory: subcategoryId || null,
    };
    setMenuItems([...menuItems, newItem]);
  };

  const updateItem = (itemId, field, value) => {
    setMenuItems(menuItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const deleteItem = (itemId) => {
    setMenuItems(menuItems.filter(item => item.id !== itemId));
  };

  const toggleCategoryEnabled = (categoryId) => {
    if (disabledCategories.includes(categoryId)) {
      setDisabledCategories(disabledCategories.filter(id => id !== categoryId));
    } else {
      setDisabledCategories([...disabledCategories, categoryId]);
    }
  };

  const deleteAllInCategory = (categoryId) => {
    if (window.confirm(t.confirmDeleteCategory || `Haluatko varmasti poistaa kaikki tuotteet kategoriasta?`)) {
      setMenuItems(menuItems.filter(item => item.category !== categoryId));
      // Also disable the category
      if (!disabledCategories.includes(categoryId)) {
        setDisabledCategories([...disabledCategories, categoryId]);
      }
      toast.success(t.categoryCleared || 'Kategoria tyhjennetty');
    }
  };

  const handleSave = async () => {
    // Validate items
    const validItems = menuItems.filter(item => item.name.trim() !== '');
    
    setSaving(true);
    try {
      // Save menu items
      const menuResponse = await authFetch(`/api/companies/${company.id}/menu`, {
        method: 'PUT',
        body: JSON.stringify(validItems),
      });
      
      // Save disabled categories
      const categoryResponse = await authFetch(`/api/companies/${company.id}`, {
        method: 'PUT',
        body: JSON.stringify({ disabled_categories: disabledCategories }),
      });
      
      if (menuResponse.ok && categoryResponse.ok) {
        toast.success(t.menuSaved || 'Menu tallennettu!');
        onSave();
      } else {
        toast.error(t.saveError || 'Virhe tallennuksessa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const isCategoryDisabled = (categoryId) => disabledCategories.includes(categoryId);

  const renderItemEditor = (item) => (
    <div key={item.id} className="flex items-center gap-2 py-2 px-3 bg-stone-50 rounded-lg">
      <Input
        value={item.name}
        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
        placeholder={t.productName || 'Tuotteen nimi'}
        className="flex-1 h-9 text-sm"
      />
      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.5"
          value={item.price}
          onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
          className="w-20 h-9 text-sm text-right"
        />
        <span className="text-sm text-stone-500">€</span>
      </div>
      <button
        onClick={() => deleteItem(item.id)}
        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">{t.menuAndPricesTitle || 'Menu ja hinnat'}</h2>
          <p className="text-sm text-stone-500 mt-1">{t.categoryToggleHint || 'Voit ottaa kokonaisia kategorioita pois käytöstä kytkimellä'}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="rounded-xl">
            {t.cancel || 'Peruuta'}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? (t.saving || 'Tallennetaan...') : (t.save || 'Tallenna')}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {categories.map(category => {
          const isDisabled = isCategoryDisabled(category.id);
          const itemCount = menuItems.filter(i => i.category === category.id).length;
          
          return (
            <div key={category.id} className={`border rounded-xl overflow-hidden transition-all ${isDisabled ? 'border-stone-200 bg-stone-50 opacity-60' : 'border-stone-200'}`}>
              <div className="flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors">
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  <span className={`font-medium ${isDisabled ? 'text-stone-400 line-through' : 'text-stone-900'}`}>{category.name}</span>
                  <span className="text-sm text-stone-500">
                    ({itemCount} {t.products || 'tuotetta'})
                  </span>
                  {isDisabled && (
                    <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full">
                      {t.disabled || 'Pois käytöstä'}
                    </span>
                  )}
                </button>
                
                <div className="flex items-center gap-2">
                  {/* Delete all in category button */}
                  {itemCount > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteAllInCategory(category.id); }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title={t.deleteCategory || 'Poista kaikki tuotteet'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* Toggle switch for category */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleCategoryEnabled(category.id); }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDisabled ? 'bg-stone-300' : 'bg-amber-500'}`}
                    title={isDisabled ? (t.enableCategory || 'Ota käyttöön') : (t.disableCategory || 'Poista käytöstä')}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDisabled ? 'translate-x-1' : 'translate-x-6'}`} />
                  </button>
                  
                  <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform ${expandedCategories[category.id] ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              {expandedCategories[category.id] && !isDisabled && (
                <div className="p-4 space-y-4">
                  {category.subcategories.length > 0 ? (
                    category.subcategories.map(sub => (
                      <div key={sub.id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-stone-700">{sub.name}</h4>
                          <button
                            onClick={() => addItem(category.id, sub.id)}
                            className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> {t.add || 'Lisää'}
                          </button>
                        </div>
                        <div className="space-y-2">
                          {getItemsByCategory(category.id, sub.id).map(renderItemEditor)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={() => addItem(category.id)}
                          className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> {t.addProduct || 'Lisää tuote'}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {getItemsByCategory(category.id).map(renderItemEditor)}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {expandedCategories[category.id] && isDisabled && (
                <div className="p-4 text-center text-stone-400">
                  <p>{t.categoryDisabledHint || 'Kategoria on pois käytöstä. Ota se käyttöön nähdäksesi tuotteet.'}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Event Types Editor
const EventTypesEditor = ({ company, onSave, onCancel, t = {} }) => {
  const [eventTypes, setEventTypes] = useState(company?.event_types || []);
  const [newType, setNewType] = useState('');
  const [loading, setLoading] = useState(false);

  const otherWhat = t.otherWhat || 'Muu, mikä?';

  const addEventType = () => {
    if (newType.trim() && !eventTypes.includes(newType.trim())) {
      setEventTypes([...eventTypes, newType.trim()]);
      setNewType('');
    }
  };

  const removeEventType = (type) => {
    setEventTypes(eventTypes.filter(t => t !== type));
  };

  const handleSave = async () => {
    // Always ensure "Muu, mikä?" is included
    let finalTypes = [...eventTypes];
    if (!finalTypes.includes('Muu, mikä?') && !finalTypes.includes('Other, what?')) {
      finalTypes.push('Muu, mikä?');
    }
    
    setLoading(true);
    try {
      const response = await authFetch(`/api/companies/${company.id}`, {
        method: 'PUT',
        body: JSON.stringify({ event_types: finalTypes }),
      });
      
      if (response.ok) {
        toast.success(t.eventTypesSaved || 'Tilaisuustyypit tallennettu!');
        onSave();
      } else {
        toast.error(t.saveError || 'Virhe tallennuksessa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-stone-900">{t.eventTypesTitle || 'Tilaisuustyypit'}</h2>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="rounded-xl">{t.cancel || 'Peruuta'}</Button>
          <Button onClick={handleSave} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
            <Save className="w-4 h-4 mr-2" />
            {loading ? (t.saving || 'Tallennetaan...') : (t.save || 'Tallenna')}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            placeholder={t.newEventType || 'Uusi tilaisuustyyppi...'}
            onKeyPress={(e) => e.key === 'Enter' && addEventType()}
          />
          <Button onClick={addEventType} variant="outline" className="rounded-xl">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-2">
          {eventTypes.map((type, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <span>{type}</span>
              {type !== 'Muu, mikä?' && type !== 'Other, what?' && (
                <button
                  onClick={() => removeEventType(type)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          
          {!eventTypes.includes('Muu, mikä?') && !eventTypes.includes('Other, what?') && (
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-amber-800">{otherWhat}</span>
              <span className="text-xs text-amber-600">{t.addedAutomatically || '(Lisätään automaattisesti)'}</span>
            </div>
          )}
        </div>

        <p className="text-sm text-stone-500">
          {t.eventTypesHelp || '"Muu, mikä?" -vaihtoehto lisätään automaattisesti. Kun asiakas valitsee sen, näytetään vapaa tekstikenttä.'}
        </p>
      </div>
    </div>
  );
};

// Services Editor
const ServicesEditor = ({ company, onSave, onCancel, t = {} }) => {
  const [services, setServices] = useState(company?.services || []);
  const [loading, setLoading] = useState(false);

  const addService = () => {
    setServices([...services, {
      id: `new-${Date.now()}`,
      name: '',
      price: 0,
      price_type: 'fixed'
    }]);
  };

  const updateService = (id, field, value) => {
    setServices(services.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const deleteService = (id) => {
    setServices(services.filter(s => s.id !== id));
  };

  const handleSave = async () => {
    const validServices = services.filter(s => s.name.trim() !== '');
    
    setLoading(true);
    try {
      const response = await authFetch(`/api/companies/${company.id}/services`, {
        method: 'PUT',
        body: JSON.stringify(validServices),
      });
      
      if (response.ok) {
        toast.success(t.servicesSaved || 'Lisäpalvelut tallennettu!');
        onSave();
      } else {
        toast.error(t.saveError || 'Virhe tallennuksessa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-stone-900">{t.servicesTitle || 'Lisäpalvelut'}</h2>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="rounded-xl">{t.cancel || 'Peruuta'}</Button>
          <Button onClick={handleSave} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
            <Save className="w-4 h-4 mr-2" />
            {loading ? (t.saving || 'Tallennetaan...') : (t.save || 'Tallenna')}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <Button onClick={addService} variant="outline" className="rounded-xl">
          <Plus className="w-4 h-4 mr-2" />
          {t.addService || 'Lisää palvelu'}
        </Button>

        <div className="space-y-3">
          {services.map(service => (
            <div key={service.id} className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
              <Input
                value={service.name}
                onChange={(e) => updateService(service.id, 'name', e.target.value)}
                placeholder={t.serviceName || 'Palvelun nimi'}
                className="flex-1"
              />
              <Input
                type="number"
                step="0.5"
                value={service.price}
                onChange={(e) => updateService(service.id, 'price', parseFloat(e.target.value) || 0)}
                className="w-24"
              />
              <span className="text-sm text-stone-500">€</span>
              <select
                value={service.price_type}
                onChange={(e) => updateService(service.id, 'price_type', e.target.value)}
                className="px-3 py-2 border border-stone-200 rounded-lg text-sm"
              >
                <option value="fixed">{t.fixed || 'Kiinteä'}</option>
                <option value="per_person">{t.perPerson || '/hlö'}</option>
                <option value="per_hour">{t.perHour || '/tunti'}</option>
              </select>
              <button
                onClick={() => deleteService(service.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Ready Menus Editor (Package menus like wedding menus)
const ReadyMenuEditor = ({ company, onSave, onCancel, t = {} }) => {
  const [readyMenus, setReadyMenus] = useState(company?.ready_menus || []);
  const [loading, setLoading] = useState(false);

  const addMenu = () => {
    setReadyMenus([...readyMenus, {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      price_per_person: 0,
      items: []
    }]);
  };

  const updateMenu = (id, field, value) => {
    setReadyMenus(readyMenus.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const updateMenuItems = (id, itemsText) => {
    // Convert comma-separated text to array
    const items = itemsText.split(',').map(i => i.trim()).filter(i => i);
    setReadyMenus(readyMenus.map(m => m.id === id ? { ...m, items } : m));
  };

  const deleteMenu = (id) => {
    setReadyMenus(readyMenus.filter(m => m.id !== id));
  };

  const handleSave = async () => {
    const validMenus = readyMenus.filter(m => m.name.trim() !== '');
    
    setLoading(true);
    try {
      const response = await authFetch(`/api/companies/${company.id}/ready-menus`, {
        method: 'PUT',
        body: JSON.stringify(validMenus),
      });
      
      if (response.ok) {
        toast.success(t.readyMenusSaved || 'Valmiit menut tallennettu!');
        onSave();
      } else {
        toast.error(t.saveError || 'Virhe tallennuksessa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">{t.readyMenusTitle || 'Valmiit menut'}</h2>
          <p className="text-sm text-stone-500">{t.readyMenusDesc || 'Pakettihinnoitellut menukokonaisuudet (esim. häämenu)'}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="rounded-xl">{t.cancel || 'Peruuta'}</Button>
          <Button onClick={handleSave} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
            <Save className="w-4 h-4 mr-2" />
            {loading ? (t.saving || 'Tallennetaan...') : (t.save || 'Tallenna')}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <Button onClick={addMenu} variant="outline" className="w-full rounded-xl border-dashed">
          <Plus className="w-4 h-4 mr-2" />
          {t.addReadyMenu || 'Lisää valmis menu'}
        </Button>

        {readyMenus.map(menu => (
          <div key={menu.id} className="border border-stone-200 rounded-xl p-4 space-y-4 bg-stone-50">
            <div className="flex items-start justify-between">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">{t.menuName || 'Menun nimi'}</label>
                  <Input
                    value={menu.name}
                    onChange={(e) => updateMenu(menu.id, 'name', e.target.value)}
                    placeholder={t.menuNamePlaceholder || 'esim. Klassinen häämenu'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">{t.pricePerPerson || 'Hinta/hlö (€)'}</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={menu.price_per_person}
                    onChange={(e) => updateMenu(menu.id, 'price_per_person', parseFloat(e.target.value) || 0)}
                    placeholder="65.00"
                  />
                </div>
              </div>
              <button
                onClick={() => deleteMenu(menu.id)}
                className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t.description || 'Kuvaus'}</label>
              <Input
                value={menu.description || ''}
                onChange={(e) => updateMenu(menu.id, 'description', e.target.value)}
                placeholder={t.descriptionPlaceholder || 'Perinteinen ja tyylikäs menukokonaisuus'}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t.contents || 'Sisältö (pilkulla eroteltu)'}</label>
              <Textarea
                value={(menu.items || []).join(', ')}
                onChange={(e) => updateMenuItems(menu.id, e.target.value)}
                placeholder={t.contentsPlaceholder || 'Alkupalat, Lohta & Naudan sisäfilettä, Jälkiruoka'}
                rows={2}
              />
              <p className="text-xs text-stone-400 mt-1">{t.contentsHelp || 'Kirjoita menuun kuuluvat kohteet pilkulla eroteltuna'}</p>
            </div>
          </div>
        ))}

        {readyMenus.length === 0 && (
          <p className="text-center text-stone-500 py-8">
            {t.noReadyMenus || 'Ei valmiita menuita. Lisää ensimmäinen klikkaamalla yllä olevaa nappia.'}
          </p>
        )}
      </div>
    </div>
  );
};

// Company Detail Component
const CompanyDetail = ({ company, onBack, onEdit, onDelete, onRefresh, onEditMenu, onEditReadyMenus, onEditEvents, onEditServices, t = {} }) => {
  const [copied, setCopied] = useState(false);
  
  // Use production URL for embed code (remove .preview from URL if present)
  const getProductionUrl = () => {
    const origin = window.location.origin;
    // If we're in preview, construct the production URL
    if (origin.includes('.preview.emergentagent.com')) {
      return origin.replace('.preview.emergentagent.com', '.emergentagent.com');
    }
    return origin;
  };
  
  const productionUrl = getProductionUrl();
  const embedUrl = `${productionUrl}/laskuri/${company.slug}`;
  const embedCode = `<iframe 
  src="${embedUrl}" 
  width="100%" 
  height="800" 
  frameborder="0"
  style="border: none; max-width: 100%;"
  title="${company.name} - Pitopalvelulaskuri"
  loading="lazy"
></iframe>`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success(t.codeCopied || 'Upotuskoodi kopioitu leikepöydälle!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!window.confirm(`${t.confirmDelete || 'Haluatko varmasti poistaa asiakkaan'} "${company.name}"?`)) return;
    
    try {
      const response = await authFetch(`/api/companies/${company.id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(t.customerDeleted || 'Asiakas poistettu');
        onDelete();
      } else {
        toast.error(t.deleteError || 'Virhe poistossa');
      }
    } catch (error) {
      toast.error(t.connectionError || 'Yhteysvirhe');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-stone-500 hover:text-stone-800 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t.back || 'Takaisin'}
        </button>
        <div className="flex gap-2">
          <Button onClick={handleDelete} variant="outline" className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" />
            {t.delete || 'Poista'}
          </Button>
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: company.primary_color }}
            >
              {company.name.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-stone-900">{company.name}</h2>
              <p className="text-stone-500">{company.slug}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-stone-600">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {company.email}
                </span>
              </div>
            </div>
          </div>
          <Button onClick={onEdit} variant="outline" className="rounded-xl">
            <Edit className="w-4 h-4 mr-2" />
            {t.editInfo || 'Muokkaa tietoja'}
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={onEditMenu}
          className="bg-white rounded-xl border border-stone-200 p-6 text-left hover:border-amber-300 hover:shadow-md transition-all"
        >
          <Menu className="w-8 h-8 text-amber-500 mb-3" />
          <h3 className="font-semibold text-stone-900">{t.menuAndPrices || 'Menu ja hinnat'}</h3>
          <p className="text-sm text-stone-500 mt-1">{company.menu_items?.length || 0} {t.products || 'tuotetta'}</p>
          <p className="text-xs text-amber-600 mt-2">{t.editMenu || 'Muokkaa ruokalistaa'} →</p>
        </button>

        <button
          onClick={onEditReadyMenus}
          className="bg-white rounded-xl border border-stone-200 p-6 text-left hover:border-amber-300 hover:shadow-md transition-all"
        >
          <FileText className="w-8 h-8 text-purple-500 mb-3" />
          <h3 className="font-semibold text-stone-900">{t.readyMenus || 'Valmiit menut'}</h3>
          <p className="text-sm text-stone-500 mt-1">{company.ready_menus?.length || 0} {t.packages || 'pakettia'}</p>
          <p className="text-xs text-amber-600 mt-2">{t.editPackages || 'Muokkaa paketteja'} →</p>
        </button>

        <button
          onClick={onEditEvents}
          className="bg-white rounded-xl border border-stone-200 p-6 text-left hover:border-amber-300 hover:shadow-md transition-all"
        >
          <Users className="w-8 h-8 text-blue-500 mb-3" />
          <h3 className="font-semibold text-stone-900">{t.eventTypes || 'Tilaisuustyypit'}</h3>
          <p className="text-sm text-stone-500 mt-1">{company.event_types?.length || 0} {t.types || 'tyyppiä'}</p>
          <p className="text-xs text-amber-600 mt-2">{t.editEvents || 'Muokkaa tilaisuuksia'} →</p>
        </button>

        <button
          onClick={onEditServices}
          className="bg-white rounded-xl border border-stone-200 p-6 text-left hover:border-amber-300 hover:shadow-md transition-all"
        >
          <Settings className="w-8 h-8 text-green-500 mb-3" />
          <h3 className="font-semibold text-stone-900">{t.additionalServices || 'Lisäpalvelut'}</h3>
          <p className="text-sm text-stone-500 mt-1">{company.services?.length || 0} {t.services || 'palvelua'}</p>
          <p className="text-xs text-amber-600 mt-2">{t.editServices || 'Muokkaa palveluita'} →</p>
        </button>
      </div>

      {/* Embed Code */}
      <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200 p-6">
        <h3 className="font-semibold text-stone-900 mb-2 flex items-center gap-2">
          <Code className="w-5 h-5 text-amber-600" />
          {t.embedCode || 'Asenna sivuillesi'}
        </h3>
        <p className="text-sm text-stone-500 mb-4">
          {t.embedInstructions || 'Liitä tämä koodi asiakkaan verkkosivuille kohtaan, jossa laskurin halutaan näkyvän.'}
        </p>
        <div className="bg-stone-900 rounded-lg p-4 font-mono text-xs text-green-400 overflow-x-auto">
          <pre className="whitespace-pre-wrap">{embedCode}</pre>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button 
            onClick={copyEmbedCode} 
            className={`rounded-xl ${copied ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? (t.copied || 'Kopioitu!') : (t.copyCode || 'Kopioi koodi')}
          </Button>
          <a href={embedUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-xl">
              <ExternalLink className="w-4 h-4 mr-2" />
              {t.openCalculator || 'Avaa laskuri'}
            </Button>
          </a>
        </div>
        <p className="text-xs text-stone-400 mt-4">
          {t.embedNote || 'Laskuri on responsiivinen ja mukautuu automaattisesti sivun leveyteen. Korkeus on oletuksena 800px.'}
        </p>
      </div>
    </div>
  );
};

// Main Admin Page Component
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [view, setView] = useState('dashboard');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [stats, setStats] = useState({ total_companies: 0, active_companies: 0, total_quotes: 0, new_quotes: 0 });
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(() => localStorage.getItem('admin_lang') || 'fi');

  // Translations
  const translations = {
    fi: {
      // Login
      loginTitle: 'Kirjaudu sisään',
      loginSubtitle: 'JuhlaLaskuri hallintapaneeli',
      username: 'Käyttäjätunnus',
      password: 'Salasana',
      login: 'Kirjaudu',
      loggingIn: 'Kirjaudutaan...',
      firstTime: 'Ensimmäinen kerta? Luo admin-käyttäjä',
      backToLogin: 'Takaisin kirjautumiseen',
      createAdmin: 'Luo admin-käyttäjä',
      createUser: 'Luo käyttäjä',
      adminCreated: 'Admin-käyttäjä luotu! Kirjaudu sisään.',
      loginSuccess: 'Kirjautuminen onnistui!',
      loginError: 'Virhe kirjautumisessa',
      // Header
      loggedIn: 'Kirjautunut:',
      changePassword: 'Vaihda salasana',
      logout: 'Kirjaudu ulos',
      loading: 'Ladataan...',
      // Dashboard
      dashboard: 'Hallintapaneeli',
      companies: 'Yritykset',
      customers: 'Asiakkaat',
      totalCompanies: 'Yrityksiä yhteensä',
      activeCompanies: 'Aktiivisia',
      totalQuotes: 'Tarjouspyyntöjä',
      newQuotes: 'Uusia',
      addCompany: 'Lisää yritys',
      noCompanies: 'Ei vielä asiakkaita. Lisää ensimmäinen asiakas!',
      active: 'Aktiivinen',
      inactive: 'Ei aktiivinen',
      products: 'tuotetta',
      // Company Detail
      calculatorUrl: 'Laskurin URL',
      copyLink: 'Kopioi linkki',
      linkCopied: 'Koodi kopioitu!',
      copied: 'Kopioitu!',
      copyCode: 'Kopioi koodi',
      openCalculator: 'Avaa laskuri',
      editInfo: 'Muokkaa tietoja',
      delete: 'Poista',
      back: 'Takaisin',
      embedCode: 'Asenna sivuillesi',
      embedInstructions: 'Liitä tämä koodi asiakkaan verkkosivuille kohtaan, jossa laskurin halutaan näkyvän.',
      embedNote: 'Laskuri on responsiivinen ja mukautuu automaattisesti sivun leveyteen. Korkeus on oletuksena 800px.',
      codeCopied: 'Upotuskoodi kopioitu leikepöydälle!',
      confirmDelete: 'Haluatko varmasti poistaa asiakkaan',
      customerDeleted: 'Asiakas poistettu',
      deleteError: 'Virhe poistossa',
      // Cards
      menuAndPrices: 'Menu ja hinnat',
      editMenu: 'Muokkaa ruokalistaa',
      readyMenus: 'Valmiit menut',
      packages: 'pakettia',
      editPackages: 'Muokkaa paketteja',
      eventTypes: 'Tilaisuustyypit',
      types: 'tyyppiä',
      editEvents: 'Muokkaa tilaisuuksia',
      additionalServices: 'Lisäpalvelut',
      services: 'palvelua',
      editServices: 'Muokkaa palveluita',
      // Form
      editBasicInfo: 'Muokkaa perustietoja',
      addNewCustomer: 'Lisää uusi asiakas',
      companyName: 'Yrityksen nimi',
      companyNameRequired: 'Yrityksen nimi *',
      slugRequired: 'Slug (URL-tunnus) *',
      slugHelp: 'Käytetään URL:ssa: /laskuri/',
      emailForQuotes: 'Sähköposti (tarjouspyyntöihin) *',
      slug: 'URL-tunnus',
      email: 'Sähköposti',
      phone: 'Puhelin',
      calculatorLanguage: 'Laskurin kieli',
      calculatorLanguageHelp: 'Tämä kieli näkyy asiakkaan laskurissa',
      allowCustomMenu: 'Salli "Rakenna oma menu"',
      allowCustomMenuHelp: 'Jos pois päältä, asiakas voi valita vain valmiita menuita',
      logo: 'Logo',
      uploadLogo: 'Lataa logo',
      orPasteUrl: 'tai',
      pasteUrl: 'Liitä URL',
      primaryColor: 'Pääväri',
      secondaryColor: 'Toissijainen väri',
      minGuests: 'Min. vierasmäärä',
      maxGuests: 'Max. vierasmäärä',
      deliveryFee: 'Toimitusmaksu (€)',
      isActive: 'Aktiivinen (laskuri näkyy)',
      save: 'Tallenna',
      saving: 'Tallennetaan...',
      cancel: 'Peruuta',
      customerUpdated: 'Asiakas päivitetty!',
      customerCreated: 'Asiakas luotu!',
      saveError: 'Virhe tallennuksessa',
      // Menu editor
      menuAndPricesTitle: 'Menu ja hinnat',
      productName: 'Tuotteen nimi',
      add: 'Lisää',
      addProduct: 'Lisää tuote',
      menuSaved: 'Menu tallennettu!',
      categoryToggleHint: 'Voit ottaa kokonaisia kategorioita pois käytöstä kytkimellä',
      disabled: 'Pois käytöstä',
      deleteCategory: 'Poista kaikki tuotteet',
      enableCategory: 'Ota käyttöön',
      disableCategory: 'Poista käytöstä',
      confirmDeleteCategory: 'Haluatko varmasti poistaa kaikki tuotteet kategoriasta?',
      categoryCleared: 'Kategoria tyhjennetty',
      categoryDisabledHint: 'Kategoria on pois käytöstä. Ota se käyttöön nähdäksesi tuotteet.',
      // Categories
      starters: 'Alkuruoat',
      salads: 'Salaatit',
      mainCourses: 'Pääruoat',
      pork: 'Possu',
      beef: 'Härkä',
      chicken: 'Kana',
      fish: 'Kala',
      vegetarian: 'Kasvis',
      soups: 'Keitot',
      sides: 'Lisukkeet',
      breads: 'Leivät',
      desserts: 'Jälkiruoat',
      drinks: 'Juomat',
      // Event types
      eventTypesTitle: 'Tilaisuustyypit',
      newEventType: 'Uusi tilaisuustyyppi...',
      eventTypesSaved: 'Tilaisuustyypit tallennettu!',
      otherWhat: 'Muu, mikä?',
      addedAutomatically: '(Lisätään automaattisesti)',
      eventTypesHelp: '"Muu, mikä?" -vaihtoehto lisätään automaattisesti. Kun asiakas valitsee sen, näytetään vapaa tekstikenttä.',
      // Services
      servicesTitle: 'Lisäpalvelut',
      addService: 'Lisää palvelu',
      serviceName: 'Palvelun nimi',
      fixed: 'Kiinteä',
      perPerson: '/hlö',
      perHour: '/tunti',
      servicesSaved: 'Lisäpalvelut tallennettu!',
      // Ready menus
      readyMenusTitle: 'Valmiit menut',
      readyMenusDesc: 'Pakettihinnoitellut menukokonaisuudet (esim. häämenu)',
      addReadyMenu: 'Lisää valmis menu',
      menuName: 'Menun nimi',
      menuNamePlaceholder: 'esim. Klassinen häämenu',
      pricePerPerson: 'Hinta/hlö (€)',
      description: 'Kuvaus',
      descriptionPlaceholder: 'Perinteinen ja tyylikäs menukokonaisuus',
      contents: 'Sisältö (pilkulla eroteltu)',
      contentsPlaceholder: 'Alkupalat, Lohta & Naudan sisäfilettä, Jälkiruoka',
      contentsHelp: 'Kirjoita menuun kuuluvat kohteet pilkulla eroteltuna',
      noReadyMenus: 'Ei valmiita menuita. Lisää ensimmäinen klikkaamalla yllä olevaa nappia.',
      readyMenusSaved: 'Valmiit menut tallennettu!',
      // Messages
      saved: 'Tallennettu!',
      error: 'Virhe',
      connectionError: 'Yhteysvirhe',
      logoUploaded: 'Logo ladattu!',
      fileTooLarge: 'Tiedosto on liian suuri (max 5MB)',
      uploadFailed: 'Lataus epäonnistui',
      passwordChanged: 'Salasana vaihdettu!',
      currentPassword: 'Nykyinen salasana',
      newPassword: 'Uusi salasana',
      confirmPassword: 'Vahvista uusi salasana',
      passwordsDontMatch: 'Uudet salasanat eivät täsmää',
      passwordTooShort: 'Salasanan tulee olla vähintään 6 merkkiä',
      changingPassword: 'Vaihdetaan...'
    },
    en: {
      // Login
      loginTitle: 'Log in',
      loginSubtitle: 'JuhlaLaskuri admin panel',
      username: 'Username',
      password: 'Password',
      login: 'Log in',
      loggingIn: 'Logging in...',
      firstTime: 'First time? Create admin user',
      backToLogin: 'Back to login',
      createAdmin: 'Create admin user',
      createUser: 'Create user',
      adminCreated: 'Admin user created! Please log in.',
      loginSuccess: 'Login successful!',
      loginError: 'Login error',
      // Header
      loggedIn: 'Logged in:',
      changePassword: 'Change password',
      logout: 'Log out',
      loading: 'Loading...',
      // Dashboard
      dashboard: 'Dashboard',
      companies: 'Companies',
      customers: 'Customers',
      totalCompanies: 'Total companies',
      activeCompanies: 'Active',
      totalQuotes: 'Quote requests',
      newQuotes: 'New',
      addCompany: 'Add company',
      noCompanies: 'No customers yet. Add the first customer!',
      active: 'Active',
      inactive: 'Inactive',
      products: 'products',
      // Company Detail
      calculatorUrl: 'Calculator URL',
      copyLink: 'Copy link',
      linkCopied: 'Code copied!',
      copied: 'Copied!',
      copyCode: 'Copy code',
      openCalculator: 'Open calculator',
      editInfo: 'Edit info',
      delete: 'Delete',
      back: 'Back',
      embedCode: 'Install on website',
      embedInstructions: 'Paste this code on the customer\'s website where you want the calculator to appear.',
      embedNote: 'The calculator is responsive and automatically adapts to the page width. Default height is 800px.',
      codeCopied: 'Embed code copied to clipboard!',
      confirmDelete: 'Are you sure you want to delete customer',
      customerDeleted: 'Customer deleted',
      deleteError: 'Delete error',
      // Cards
      menuAndPrices: 'Menu & prices',
      editMenu: 'Edit menu',
      readyMenus: 'Ready menus',
      packages: 'packages',
      editPackages: 'Edit packages',
      eventTypes: 'Event types',
      types: 'types',
      editEvents: 'Edit events',
      additionalServices: 'Additional services',
      services: 'services',
      editServices: 'Edit services',
      // Form
      editBasicInfo: 'Edit basic info',
      addNewCustomer: 'Add new customer',
      companyName: 'Company name',
      companyNameRequired: 'Company name *',
      slugRequired: 'Slug (URL identifier) *',
      slugHelp: 'Used in URL: /laskuri/',
      emailForQuotes: 'Email (for quote requests) *',
      slug: 'URL identifier',
      email: 'Email',
      phone: 'Phone',
      calculatorLanguage: 'Calculator language',
      calculatorLanguageHelp: 'This language will be shown in the customer\'s calculator',
      allowCustomMenu: 'Allow "Build your own menu"',
      allowCustomMenuHelp: 'If off, customer can only select ready menus',
      logo: 'Logo',
      uploadLogo: 'Upload logo',
      orPasteUrl: 'or',
      pasteUrl: 'Paste URL',
      primaryColor: 'Primary color',
      secondaryColor: 'Secondary color',
      minGuests: 'Min. guests',
      maxGuests: 'Max. guests',
      deliveryFee: 'Delivery fee (€)',
      isActive: 'Active (calculator visible)',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      customerUpdated: 'Customer updated!',
      customerCreated: 'Customer created!',
      saveError: 'Save error',
      // Menu editor
      menuAndPricesTitle: 'Menu & prices',
      productName: 'Product name',
      add: 'Add',
      addProduct: 'Add product',
      menuSaved: 'Menu saved!',
      categoryToggleHint: 'You can disable entire categories using the toggle switch',
      disabled: 'Disabled',
      deleteCategory: 'Delete all products',
      enableCategory: 'Enable',
      disableCategory: 'Disable',
      confirmDeleteCategory: 'Are you sure you want to delete all products from this category?',
      categoryCleared: 'Category cleared',
      categoryDisabledHint: 'Category is disabled. Enable it to see the products.',
      // Categories
      starters: 'Starters',
      salads: 'Salads',
      mainCourses: 'Main courses',
      pork: 'Pork',
      beef: 'Beef',
      chicken: 'Chicken',
      fish: 'Fish',
      vegetarian: 'Vegetarian',
      soups: 'Soups',
      sides: 'Sides',
      breads: 'Breads',
      desserts: 'Desserts',
      drinks: 'Drinks',
      // Event types
      eventTypesTitle: 'Event types',
      newEventType: 'New event type...',
      eventTypesSaved: 'Event types saved!',
      otherWhat: 'Other, what?',
      addedAutomatically: '(Added automatically)',
      eventTypesHelp: '"Other, what?" option is added automatically. When customer selects it, a free text field is shown.',
      // Services
      servicesTitle: 'Additional services',
      addService: 'Add service',
      serviceName: 'Service name',
      fixed: 'Fixed',
      perPerson: '/person',
      perHour: '/hour',
      servicesSaved: 'Services saved!',
      // Ready menus
      readyMenusTitle: 'Ready menus',
      readyMenusDesc: 'Package-priced menu options (e.g., wedding menu)',
      addReadyMenu: 'Add ready menu',
      menuName: 'Menu name',
      menuNamePlaceholder: 'e.g., Classic wedding menu',
      pricePerPerson: 'Price/person (€)',
      description: 'Description',
      descriptionPlaceholder: 'Traditional and elegant menu package',
      contents: 'Contents (comma separated)',
      contentsPlaceholder: 'Appetizers, Salmon & Beef tenderloin, Dessert',
      contentsHelp: 'Enter menu items separated by commas',
      noReadyMenus: 'No ready menus. Click the button above to add one.',
      readyMenusSaved: 'Ready menus saved!',
      // Messages
      saved: 'Saved!',
      error: 'Error',
      connectionError: 'Connection error',
      logoUploaded: 'Logo uploaded!',
      fileTooLarge: 'File is too large (max 5MB)',
      uploadFailed: 'Upload failed',
      passwordChanged: 'Password changed!',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      passwordsDontMatch: 'New passwords don\'t match',
      passwordTooShort: 'Password must be at least 6 characters',
      changingPassword: 'Changing...'
    }
  };

  const t = translations[language];

  // Save language preference
  useEffect(() => {
    localStorage.setItem('admin_lang', language);
  }, [language]);

  useEffect(() => {
    const token = getToken();
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await authFetch('/api/admin/me');
      if (response.ok) {
        const data = await response.json();
        setIsLoggedIn(true);
        setUsername(data.username);
        fetchData();
      } else {
        removeToken();
      }
    } catch (error) {
      removeToken();
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [companiesRes, statsRes] = await Promise.all([
        authFetch('/api/companies'),
        authFetch('/api/stats'),
      ]);
      
      if (companiesRes.ok) {
        setCompanies(await companiesRes.json());
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const refreshCompany = async () => {
    if (selectedCompany) {
      const response = await authFetch(`/api/admin/companies/${selectedCompany.id}`);
      if (response.ok) {
        const updated = await response.json();
        setSelectedCompany(updated);
        setCompanies(companies.map(c => c.id === updated.id ? updated : c));
      }
    }
    fetchData();
  };

  const handleLogin = (name) => {
    setIsLoggedIn(true);
    setUsername(name);
    fetchData();
  };

  const handleLogout = () => {
    removeToken();
    setIsLoggedIn(false);
    setUsername('');
  };

  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setView('detail');
  };

  const handleSaveCompany = (company) => {
    fetchData();
    setView('dashboard');
    setSelectedCompany(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-stone-500">{t.loading || 'Ladataan...'}</div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginForm onLogin={handleLogin} t={t} />;
  }

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Top Bar */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-stone-900">JuhlaLaskuri Admin</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 border border-stone-200 rounded-lg p-1">
              <button
                onClick={() => setLanguage('fi')}
                className={`w-8 h-6 rounded overflow-hidden transition-all ${language === 'fi' ? 'ring-2 ring-amber-500 scale-105' : 'opacity-60 hover:opacity-100'}`}
                title="Suomi"
              >
                <svg viewBox="0 0 1800 1100" className="w-full h-full">
                  <rect fill="#fff" width="1800" height="1100"/>
                  <rect fill="#003580" y="400" width="1800" height="300"/>
                  <rect fill="#003580" x="500" width="300" height="1100"/>
                </svg>
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`w-8 h-6 rounded overflow-hidden transition-all ${language === 'en' ? 'ring-2 ring-amber-500 scale-105' : 'opacity-60 hover:opacity-100'}`}
                title="English"
              >
                <svg viewBox="0 0 60 30" className="w-full h-full">
                  <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
                  <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
                  <g clipPath="url(#s)">
                    <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
                    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
                    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
                    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
                    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
                  </g>
                </svg>
              </button>
            </div>
            <span className="text-sm text-stone-500">{t.loggedIn} {username}</span>
            <Button onClick={() => setShowPasswordModal(true)} variant="outline" size="sm" className="rounded-lg">
              <Settings className="w-4 h-4 mr-2" />
              {t.changePassword}
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="rounded-lg">
              <LogOut className="w-4 h-4 mr-2" />
              {t.logout}
            </Button>
          </div>
        </div>
      </header>

      {/* Password Change Modal */}
      <ChangePasswordModal 
        isOpen={showPasswordModal} 
        onClose={() => setShowPasswordModal(false)}
        t={t}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {view === 'dashboard' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-bold text-stone-900">{t.dashboard}</h1>
              <Button 
                onClick={() => setView('new')}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.addCompany}
              </Button>
            </div>
            <Dashboard 
              stats={stats} 
              companies={companies} 
              onSelectCompany={handleSelectCompany}
              t={t}
            />
          </>
        )}

        {view === 'new' && (
          <CompanyForm 
            onSave={handleSaveCompany}
            onCancel={() => setView('dashboard')}
            t={t}
          />
        )}

        {view === 'edit' && selectedCompany && (
          <CompanyForm 
            company={selectedCompany}
            onSave={() => { refreshCompany(); setView('detail'); }}
            onCancel={() => setView('detail')}
            t={t}
          />
        )}

        {view === 'detail' && selectedCompany && (
          <CompanyDetail 
            company={selectedCompany}
            onBack={() => { setView('dashboard'); setSelectedCompany(null); }}
            onEdit={() => setView('edit')}
            onDelete={() => { setView('dashboard'); setSelectedCompany(null); fetchData(); }}
            onRefresh={refreshCompany}
            onEditMenu={() => setView('editMenu')}
            onEditReadyMenus={() => setView('editReadyMenus')}
            onEditEvents={() => setView('editEvents')}
            onEditServices={() => setView('editServices')}
            t={t}
          />
        )}

        {view === 'editMenu' && selectedCompany && (
          <MenuEditor
            company={selectedCompany}
            onSave={() => { refreshCompany(); setView('detail'); }}
            onCancel={() => setView('detail')}
            t={t}
          />
        )}

        {view === 'editReadyMenus' && selectedCompany && (
          <ReadyMenuEditor
            company={selectedCompany}
            onSave={() => { refreshCompany(); setView('detail'); }}
            onCancel={() => setView('detail')}
            t={t}
          />
        )}

        {view === 'editEvents' && selectedCompany && (
          <EventTypesEditor
            company={selectedCompany}
            onSave={() => { refreshCompany(); setView('detail'); }}
            onCancel={() => setView('detail')}
            t={t}
          />
        )}

        {view === 'editServices' && selectedCompany && (
          <ServicesEditor
            company={selectedCompany}
            onSave={() => { refreshCompany(); setView('detail'); }}
            onCancel={() => setView('detail')}
            t={t}
          />
        )}
      </main>
    </div>
  );
};

export default AdminPage;
